import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApiService, PnlByRegimeDay, StrategyRegimeReviewData } from '../../services/adminApiService';
import AiTuneChangeTable from './AiTuneChangeTable';

const RANGE_OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

function money(n: number | string | undefined | null): string {
  const v = Number(n ?? 0);
  const prefix = v >= 0 ? '+' : '-';
  return `${prefix}$${Math.abs(v).toFixed(2)}`;
}

function pct(n: number | string | undefined | null): string {
  return `${Number(n ?? 0).toFixed(1)}%`;
}

function formatRegime(code: string | undefined, labels?: Record<string, string>): string {
  if (!code) return '—';
  return labels?.[code] || code.replace(/_/g, ' ');
}

function snapshotToChanges(snapshot?: Record<string, unknown> | null): Array<{
  field?: string;
  label?: string;
  before?: string | null;
  after?: string | null;
}> {
  if (!snapshot) return [];
  const priority = [
    'selectedCoins',
    'performanceExcludeCoins',
    'trailingStopLossPercent',
    'takeProfitPercent',
    'basketTakeProfitPercent',
    'rsiFilterEnabled',
    'btcCorrelationFilterEnabled',
    'participationFilterEnabled',
    'microReversalFilterEnabled',
    'aiAutoTuneEnabled',
  ];
  const rows: Array<{ field?: string; label?: string; before?: string | null; after?: string | null }> = [];
  const seen = new Set<string>();
  for (const key of priority) {
    if (Object.prototype.hasOwnProperty.call(snapshot, key)) {
      rows.push({
        field: key,
        label: key,
        before: null,
        after: formatSnapshotValue(snapshot[key]),
      });
      seen.add(key);
    }
  }
  Object.entries(snapshot).forEach(([key, value]) => {
    if (seen.has(key)) return;
    rows.push({
      field: key,
      label: key,
      before: null,
      after: formatSnapshotValue(value),
    });
  });
  return rows.slice(0, 24);
}

function formatSnapshotValue(value: unknown): string {
  if (value == null) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const REGIME_COLORS: Record<string, string> = {
  HOT_MEME: '#059669',
  MODERATE: '#2563eb',
  HIGH_VOLATILITY: '#d97706',
  LOW_PARTICIPATION: '#f59e0b',
  BEARISH_BTC: '#dc2626',
  UNKNOWN: '#9ca3af',
};

function regimeColor(regime: string): string {
  return REGIME_COLORS[regime] || '#6b7280';
}

interface PnlByRegimeChartProps {
  rows: PnlByRegimeDay[];
  regimeLabels?: Record<string, string>;
}

const PnlByRegimeChart: React.FC<PnlByRegimeChartProps> = ({ rows, regimeLabels }) => {
  const maxAbs = useMemo(() => {
    const vals = rows.map((r) => Math.abs(Number(r.netPnl)));
    return Math.max(...vals, 0.01);
  }, [rows]);

  const BAR_MAX_H = 56;

  return (
    <div style={{ marginBottom: 20 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#111827' }}>Daily PnL by regime</h4>
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 12px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, minHeight: BAR_MAX_H + 32 }}>
          {rows.map((row) => {
            const val = Number(row.netPnl);
            const barH = Math.max(4, Math.round((Math.abs(val) / maxAbs) * BAR_MAX_H));
            const color = regimeColor(row.regime);
            const label = regimeLabels?.[row.regime] || row.regime.replace(/_/g, ' ');
            const sign = val >= 0 ? '+' : '-';
            return (
              <div
                key={row.date}
                title={`${row.date} · ${label}\nPnL: ${sign}$${Math.abs(val).toFixed(2)}\nCloses: ${row.closeCount}`}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 18 }}
              >
                <div
                  style={{
                    width: 14,
                    height: barH,
                    background: color,
                    opacity: val < 0 ? 0.5 : 0.85,
                    borderRadius: val >= 0 ? '3px 3px 0 0' : '0 0 3px 3px',
                  }}
                />
                <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2, transform: 'rotate(-45deg)', transformOrigin: 'top left', whiteSpace: 'nowrap' }}>
                  {row.date.slice(5)}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 20, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
          {Object.entries(REGIME_COLORS).filter(([k]) => k !== 'UNKNOWN').map(([regime, color]) => (
            <span key={regime} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#374151' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: 'inline-block' }} />
              {regimeLabels?.[regime] || regime.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export interface AiTuneRegimeReviewProps {
  strategyId: number;
  active: boolean;
}

const AiTuneRegimeReview: React.FC<AiTuneRegimeReviewProps> = ({ strategyId, active }) => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<StrategyRegimeReviewData | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!strategyId) return;
    setLoading(true);
    setError(null);
    try {
      const to = new Date();
      const from = new Date();
      from.setUTCDate(from.getUTCDate() - (days - 1));
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      const res = await adminApiService.getRegimeReview(strategyId, fmt(from), fmt(to));
      if (res.success && res.data) {
        setReview(res.data);
        setSelectedDate((prev) => {
          if (prev && res.data?.dailySummaries?.some((d) => d.date === prev)) return prev;
          return res.data?.dailySummaries?.[0]?.date || null;
        });
      } else {
        setReview(null);
        setError(res.error || 'Failed to load regime review');
      }
    } catch (e: unknown) {
      setReview(null);
      setError(e instanceof Error ? e.message : 'Failed to load regime review');
    } finally {
      setLoading(false);
    }
  }, [strategyId, days]);

  useEffect(() => {
    if (active) {
      load();
    }
  }, [active, load]);

  const selectedDay = useMemo(
    () => review?.dailySummaries?.find((d) => d.date === selectedDate) || null,
    [review, selectedDate]
  );

  if (!active) return null;

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: '1px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '8px 10px',
    borderBottom: '1px solid #f3f4f6',
    color: '#111827',
    verticalAlign: 'top',
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
        Daily bot PnL with AI tune overlay, regime performance matrix, and exit-reason breakdown.
        Regime on older trades is inferred from tune history until backfill runs.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setDays(opt.value)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: days === opt.value ? '2px solid #7c3aed' : '1px solid #e5e7eb',
              background: days === opt.value ? '#faf5ff' : '#fff',
              color: days === opt.value ? '#6d28d9' : '#374151',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            background: '#fff',
            fontSize: 12,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {review && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 12, padding: '6px 10px', borderRadius: 999, background: '#f3f4f6' }}>
            Closes: <strong>{review.totalCloses}</strong>
          </span>
          <span style={{ fontSize: 12, padding: '6px 10px', borderRadius: 999, background: '#f3f4f6' }}>
            Net PnL: <strong>{money(review.totalNetPnl)}</strong>
          </span>
          <span style={{ fontSize: 12, padding: '6px 10px', borderRadius: 999, background: '#ecfdf5' }}>
            Stored regime: <strong>{review.storedRegimeCount}</strong>
          </span>
          <span style={{ fontSize: 12, padding: '6px 10px', borderRadius: 999, background: '#fffbeb' }}>
            Inferred: <strong>{review.inferredRegimeCount}</strong>
          </span>
          <span style={{ fontSize: 12, padding: '6px 10px', borderRadius: 999, background: '#f9fafb' }}>
            Unknown: <strong>{review.unknownRegimeCount}</strong>
          </span>
        </div>
      )}

      {review && review.regimeMatrix.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#111827' }}>Regime performance matrix</h4>
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Regime</th>
                  <th style={{ ...thStyle, textAlign: 'center' }} title="Days this was the dominant regime in the selected range">Days</th>
                  <th style={thStyle}>Trades</th>
                  <th style={thStyle}>Net PnL</th>
                  <th style={thStyle}>Win %</th>
                  <th style={thStyle}>Avg PnL</th>
                  <th style={thStyle}>Best coin</th>
                  <th style={thStyle}>Worst coin</th>
                  <th style={thStyle}>Source</th>
                </tr>
              </thead>
              <tbody>
                  {review.regimeMatrix.map((row) => (
                  <tr key={row.regime}>
                    <td style={tdStyle}>{formatRegime(row.regime, review.regimeLabels)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}
                        title="Days this was the dominant regime in the selected range">
                      {row.dominantDayCount ?? '—'}
                    </td>
                    <td style={tdStyle}>{row.tradeCount}</td>
                    <td style={{ ...tdStyle, color: Number(row.netPnl) >= 0 ? '#059669' : '#dc2626' }}>
                      {money(row.netPnl)}
                    </td>
                    <td style={tdStyle}>{pct(row.winRate)}</td>
                    <td style={tdStyle}>{money(row.avgPnl)}</td>
                    <td style={tdStyle}>{row.bestCoin || '—'}</td>
                    <td style={tdStyle}>{row.worstCoin || '—'}</td>
                    <td style={tdStyle}>
                      {row.storedCount}/{row.inferredCount} stored/inferred
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {review && review.exitReasonMatrix.length > 0 && (() => {
        const hasScalperTp = review.exitReasonMatrix.some((r) => (r.scalperTp ?? 0) > 0);
        return (
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#111827' }}>Regime × exit reason</h4>
            {hasScalperTp && (
              <p style={{ margin: '0 0 6px', fontSize: 11, color: '#b45309', fontWeight: 600 }}>
                ⚡ Scalper TP column visible — scalper mode was active during this window
              </p>
            )}
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Regime</th>
                    <th style={thStyle}>Micro TSL</th>
                    <th style={thStyle}>Classic TSL</th>
                    <th style={thStyle}>Stop loss</th>
                    <th style={thStyle}>Take profit</th>
                    {hasScalperTp && <th style={{ ...thStyle, color: '#b45309' }} title="Closes that fired via scalper-mode take-profit">⚡ Scalper TP</th>}
                    <th style={thStyle}>Other</th>
                  </tr>
                </thead>
                <tbody>
                  {review.exitReasonMatrix.map((row) => (
                    <tr key={row.regime}>
                      <td style={tdStyle}>{formatRegime(row.regime, review.regimeLabels)}</td>
                      <td style={tdStyle}>{row.microTsl}</td>
                      <td style={tdStyle}>{row.classicTsl}</td>
                      <td style={tdStyle}>{row.classicSl}</td>
                      <td style={tdStyle}>{row.takeProfit}</td>
                      {hasScalperTp && (
                        <td style={{ ...tdStyle, color: (row.scalperTp ?? 0) > 0 ? '#b45309' : undefined, fontWeight: (row.scalperTp ?? 0) > 0 ? 700 : undefined }}>
                          {row.scalperTp ?? 0}
                        </td>
                      )}
                      <td style={tdStyle}>{row.other}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {review && review.pnlByRegimeByDay && review.pnlByRegimeByDay.length > 0 && (
        <PnlByRegimeChart rows={review.pnlByRegimeByDay} regimeLabels={review.regimeLabels} />
      )}

      {review && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#111827' }}>Daily summary</h4>
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Closes</th>
                  <th style={thStyle}>Net PnL</th>
                  <th style={thStyle}>Win %</th>
                  <th style={thStyle}>Regime</th>
                  <th style={thStyle}>Tune events</th>
                </tr>
              </thead>
              <tbody>
                {review.dailySummaries.map((day) => {
                  const selected = day.date === selectedDate;
                  return (
                    <tr
                      key={day.date}
                      onClick={() => setSelectedDate(day.date)}
                      style={{ cursor: 'pointer', background: selected ? '#faf5ff' : undefined }}
                    >
                      <td style={tdStyle}>{day.date}</td>
                      <td style={tdStyle}>{day.closeCount}</td>
                      <td style={{ ...tdStyle, color: Number(day.netPnl) >= 0 ? '#059669' : '#dc2626' }}>
                        {money(day.netPnl)}
                      </td>
                      <td style={tdStyle}>{pct(day.winRate)}</td>
                      <td style={tdStyle}>{formatRegime(day.dominantRegime, review.regimeLabels)}</td>
                      <td style={tdStyle}>{day.tuneEventCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedDay && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fff' }}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#111827' }}>
            Drill-down: {selectedDay.date}
          </h4>

          {selectedDay.tuneEvents.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#374151' }}>AI tune events</p>
              {selectedDay.tuneEvents.map((event) => {
                const gateLabels: string[] = [];
                if (event.gateChanges) {
                  if (event.gateChanges.includes('longBlockOn'))   gateLabels.push('🔒 Short-only mode ON');
                  if (event.gateChanges.includes('longBlockOff'))  gateLabels.push('🔓 Short-only mode OFF');
                  if (event.gateChanges.includes('entryBlockOn'))  gateLabels.push('⛔ Entry block ON');
                  if (event.gateChanges.includes('entryBlockOff')) gateLabels.push('✅ Entry block OFF');
                  if (event.gateChanges.includes('scalperOn'))     gateLabels.push('⚡ Scalper mode ON');
                  if (event.gateChanges.includes('scalperOff'))    gateLabels.push('⚡ Scalper mode OFF');
                }
                return (
                <div
                  key={event.id}
                  style={{
                    padding: '8px 10px',
                    marginBottom: 8,
                    borderRadius: 8,
                    background: gateLabels.length > 0 ? '#eff6ff' : '#f8fafc',
                    fontSize: 12,
                    color: '#374151',
                    borderLeft: gateLabels.length > 0 ? '3px solid #2563eb' : undefined,
                  }}
                >
                  <strong>{event.appliedAt ? new Date(event.appliedAt).toLocaleString() : '—'}</strong>
                  {' · '}
                  {formatRegime(event.marketRegime, review?.regimeLabels)} · {event.outcome || '—'} ·{' '}
                  {event.changeCount} change(s)
                  {event.netPnlAtApply != null && event.netPnlAfter != null && (
                    <span>
                      {' '}· At apply {money(event.netPnlAtApply)} → after {money(event.netPnlAfter)}
                    </span>
                  )}
                  {gateLabels.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {gateLabels.map((lbl) => (
                        <span key={lbl} style={{ padding: '1px 7px', borderRadius: 999, background: '#dbeafe', color: '#1d4ed8', fontSize: 11, fontWeight: 600 }}>{lbl}</span>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {selectedDay.configSnapshotAfter && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                Config as of end of day (last applied tune)
              </p>
              <AiTuneChangeTable changes={snapshotToChanges(selectedDay.configSnapshotAfter)} />
            </div>
          )}

          {selectedDay.trades.length > 0 ? (
            <div>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                Trades ({selectedDay.trades.length})
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Symbol</th>
                      <th style={thStyle}>PnL</th>
                      <th style={thStyle}>Exit</th>
                      <th style={thStyle}>Regime</th>
                      <th style={thStyle}>Closed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDay.trades.map((trade) => (
                      <tr key={trade.id ?? `${trade.symbol}-${trade.closedAt}`}>
                        <td style={tdStyle}>{trade.symbol}</td>
                        <td style={{ ...tdStyle, color: Number(trade.pnl) >= 0 ? '#059669' : '#dc2626' }}>
                          {money(trade.pnl)}
                        </td>
                        <td style={tdStyle}>{trade.exitCategory || trade.exitReason || '—'}</td>
                        <td style={tdStyle}>
                          {formatRegime(trade.regime, review?.regimeLabels)}
                          {trade.regimeSource ? ` (${trade.regimeSource})` : ''}
                        </td>
                        <td style={tdStyle}>
                          {trade.closedAt ? new Date(trade.closedAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>No closes recorded for this day.</p>
          )}
        </div>
      )}

      {!loading && review && review.totalCloses === 0 && review.dailySummaries.every((d) => d.tuneEventCount === 0) && (
        <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, color: '#6b7280', fontSize: 13 }}>
          No closed trades or tune events in this range.
        </div>
      )}
    </div>
  );
};

export default AiTuneRegimeReview;
